import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Container, Row, Col, Table, Button, Badge, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import RegistrationService from '../../services/registration.service';
import { getAllSubsites, allSubsites } from '../../reducers/subsite';

const AdminRegistrations = () => {
  const dispatch = useDispatch();
  const [registrationRequests, setRegistrationRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDenyModal, setShowDenyModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedSubsite, setSelectedSubsite] = useState('');
  const [denyReason, setDenyReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const subsites = useSelector(allSubsites);

  useEffect(() => {
    loadRegistrationRequests();
    dispatch(getAllSubsites({range: [1, 1000]}));
  }, [dispatch]);

  const loadRegistrationRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await RegistrationService.getRegistrationRequests();
      setRegistrationRequests(response.data || []);
    } catch (error) {
      console.error('Error loading registration requests:', error);
      setError('Failed to load registration requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveClick = (request) => {
    setSelectedRequest(request);
    setSelectedSubsite('');
    setShowApproveModal(true);
  };

  const handleApproveConfirm = async () => {
    if (!selectedRequest || !selectedSubsite) {
      setError('Please select a subsite');
      return;
    }

    try {
      setActionLoading(true);
      await RegistrationService.approveRegistrationRequest(selectedRequest.id, selectedSubsite);
      setSuccessMessage('Registration request approved successfully');
      setShowApproveModal(false);
      setSelectedRequest(null);
      setSelectedSubsite('');
      loadRegistrationRequests(); // Reload the list
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error approving registration request:', error);
      setError(error.response?.data?.message || 'Failed to approve registration request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDenyClick = (request) => {
    setSelectedRequest(request);
    setDenyReason('');
    setShowDenyModal(true);
  };

  const handleDenyConfirm = async () => {
    if (!selectedRequest || !denyReason.trim()) {
      setError('Please provide a reason for denial');
      return;
    }

    try {
      setActionLoading(true);
      await RegistrationService.denyRegistrationRequest(selectedRequest.id, denyReason);
      setSuccessMessage('Registration request denied successfully');
      setShowDenyModal(false);
      setSelectedRequest(null);
      setDenyReason('');
      loadRegistrationRequests(); // Reload the list
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error denying registration request:', error);
      setError('Failed to deny registration request');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container>
        <Row>
          <Col>
            <h2>Registration Requests</h2>
            <div className="text-center">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container>
      <Row>
        <Col>
          <h2>Registration Requests</h2>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>
              {successMessage}
            </Alert>
          )}

          {registrationRequests.length === 0 ? (
            <Alert variant="info">
              No pending registration requests found.
            </Alert>
          ) : (
            <Table striped bordered hover responsive>
              <thead className="table-dark">
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Company</th>
                  <th>Region</th>
                  <th>Request Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {registrationRequests.map((request) => (
                  <tr key={request.id}>
                    <td>{request.firstName} {request.lastName}</td>
                    <td>{request.email}</td>
                    <td>{request.company || 'N/A'}</td>
                    <td>{request.region || 'N/A'}</td>
                    <td>{formatDate(request.createdAt)}</td>
                    <td>
                      <Badge bg="warning">Pending</Badge>
                    </td>
                    <td>
                      <Button
                        variant="success"
                        size="sm"
                        className="me-2"
                        onClick={() => handleApproveClick(request)}
                        disabled={actionLoading}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDenyClick(request)}
                        disabled={actionLoading}
                      >
                        Deny
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Col>
      </Row>

      {/* Approve Modal */}
      <Modal show={showApproveModal} onHide={() => setShowApproveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Approve Registration Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <div>
              <Alert variant="info">
                <p className="mb-1"><strong>Name:</strong> {selectedRequest.firstName} {selectedRequest.lastName}</p>
                <p className="mb-1"><strong>Email:</strong> {selectedRequest.email}</p>
                <p className="mb-1"><strong>Company:</strong> {selectedRequest.company || 'N/A'}</p>
                <p className="mb-0"><strong>Region:</strong> {selectedRequest.region || 'N/A'}</p>
              </Alert>
              <Form.Group className="mb-3">
                <Form.Label>Select Subsite *</Form.Label>
                <Form.Select
                  value={selectedSubsite}
                  onChange={(e) => setSelectedSubsite(e.target.value)}
                  required
                >
                  <option value="">Choose a subsite...</option>
                  {subsites.map(subsite => (
                    <option key={subsite.id} value={subsite.id}>
                      {subsite.subsiteName}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  The user will be added to the selected subsite. If the user already exists, they will be added to this subsite. If they are new, a registration email will be sent.
                </Form.Text>
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowApproveModal(false)}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleApproveConfirm}
            disabled={actionLoading || !selectedSubsite}
          >
            {actionLoading ? <Spinner animation="border" size="sm" /> : 'Approve & Add to Subsite'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Deny Modal */}
      <Modal show={showDenyModal} onHide={() => setShowDenyModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Deny Registration Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <div>
              <p><strong>Name:</strong> {selectedRequest.firstName} {selectedRequest.lastName}</p>
              <p><strong>Email:</strong> {selectedRequest.email}</p>
              <Form.Group className="mb-3">
                <Form.Label>Reason for Denial *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={denyReason}
                  onChange={(e) => setDenyReason(e.target.value)}
                  placeholder="Please provide a reason for denying this registration request..."
                  required
                />
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDenyModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDenyConfirm}
            disabled={actionLoading || !denyReason.trim()}
          >
            {actionLoading ? <Spinner animation="border" size="sm" /> : 'Deny Request'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminRegistrations;

